import { useRef, useState } from 'react'

/**
 * Horizontal swipe on a list row, as on iOS.
 *
 * Pointer events rather than touch events, so it also works with a trackpad,
 * and it releases the gesture the moment the movement looks vertical — otherwise
 * swiping would fight scrolling, which is the usual way this goes wrong.
 */
export interface SwipeHandlers {
  offset: number
  bind: {
    onPointerDown: (e: React.PointerEvent) => void
    onPointerMove: (e: React.PointerEvent) => void
    onPointerUp: (e: React.PointerEvent) => void
    onPointerCancel: (e: React.PointerEvent) => void
  }
  reset: () => void
}

const TRIGGER = 72      // how far before an action fires
const MAX = 96          // rubber-band ceiling
const SLOP = 10         // movement before we decide the axis

export function useSwipe(
  onLeft?: () => void,
  onRight?: () => void,
): SwipeHandlers {
  const [offset, setOffset] = useState(0)
  const start = useRef<{ x: number; y: number } | null>(null)
  const axis = useRef<'none' | 'x' | 'y'>('none')

  const finish = (fire: boolean) => {
    if (fire && offset <= -TRIGGER) onLeft?.()
    if (fire && offset >= TRIGGER) onRight?.()
    setOffset(0)
    start.current = null
    axis.current = 'none'
  }

  return {
    offset,
    reset: () => finish(false),
    bind: {
      onPointerDown: (e) => {
        if (e.pointerType === 'mouse') return   // a mouse drag is not a swipe
        start.current = { x: e.clientX, y: e.clientY }
        axis.current = 'none'
      },
      onPointerMove: (e) => {
        const from = start.current
        if (!from) return
        const dx = e.clientX - from.x
        const dy = e.clientY - from.y
        if (axis.current === 'none') {
          if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return
          // Vertical wins ties: scrolling must stay effortless.
          axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
          if (axis.current === 'y') { start.current = null; return }
        }
        const clamped = Math.max(-MAX, Math.min(MAX, dx))
        setOffset(clamped)
      },
      onPointerUp: () => finish(true),
      onPointerCancel: () => finish(false),
    },
  }
}

export const SWIPE_TRIGGER = TRIGGER
